import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { BrandLogo } from "../components/BrandLogo";
import { BlogCover } from "../components/BlogCover";
import { useApp } from "../context/AppContext";

const Blog = () => {
    const navigate = useNavigate();
    const { language } = useApp();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const isEs = language === "es";

    useEffect(() => {
        // SEO — set page title + meta for the listing page.
        const t = isEs
            ? "Blog Caesar — Finanzas personales sin corbata | Vive a lo Grande"
            : "Caesar Blog — Personal finance without the suit | Vive a lo Grande";
        document.title = t;
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        const desc = isEs
            ? "Consejos casuales de finanzas personales y seguros de vida a término — protección, no inversión. Publicado por Caesar cada 2 días."
            : "Casual personal-finance tips and term life insurance guidance — protection, not investment. Written by Caesar, fresh every 2 days.";
        setMeta("description", desc);
        setMeta("og:title", t, "property");
        setMeta("og:description", desc, "property");
        setMeta("og:type", "website", "property");
    }, [isEs]);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/blog/posts?limit=30");
                setPosts(res.data.posts || []);
            } catch (e) {
                setPosts([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const fmtDate = (iso) => {
        if (!iso) return "";
        try {
            const d = new Date(iso);
            return d.toLocaleDateString(isEs ? "es-ES" : "en-US", { year: "numeric", month: "short", day: "numeric" });
        } catch {
            return "";
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="blog-page">
            <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/85 backdrop-blur border-b border-[hsl(var(--border))]">
                <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <button
                        data-testid="blog-back-button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {isEs ? "Volver" : "Back"}
                    </button>
                    <button
                        onClick={() => navigate("/")}
                        className="flex items-center gap-2 text-[hsl(var(--primary))]"
                        data-testid="blog-brand-home"
                    >
                        <BrandLogo size={26} />
                        <span className="font-display font-bold text-base tracking-tight text-[hsl(var(--foreground))]">
                            Vive a lo Grande
                        </span>
                    </button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--accent))] uppercase">
                            {isEs ? "Blog de Caesar" : "Caesar's Blog"}
                        </span>
                        <span className="h-px w-14 bg-[hsl(var(--muted-foreground))]/40" />
                    </div>
                    <h1 className="font-display font-extrabold text-[hsl(var(--foreground))] leading-[1.05] tracking-[-0.02em] text-4xl md:text-6xl max-w-3xl">
                        {isEs ? "Finanzas personales, sin la corbata." : "Personal finance, without the suit."}
                    </h1>
                    <p className="mt-4 text-[hsl(var(--muted-foreground))] max-w-2xl leading-relaxed">
                        {isEs
                            ? "Post nuevo cada 2 días. Consejos casuales sobre presupuesto, deudas, fondos de emergencia y por qué el seguro de vida a término es protección — no inversión."
                            : "A fresh post every 2 days. Casual takes on budgeting, debt, emergency funds and why term life insurance is protection — not an investment."}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-xs text-[hsl(var(--muted-foreground))]">
                        <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                        {isEs ? "Escrito por Caesar · Coach de dinero" : "Written by Caesar · Vive a lo Grande money coach"}
                    </div>
                </motion.div>

                <section className="mt-14 md:mt-20">
                    {loading ? (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="h-56 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/40 animate-pulse" />
                            ))}
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-16 text-[hsl(var(--muted-foreground))]">
                            {isEs ? "Aún no hay posts. Vuelve pronto." : "No posts yet — check back soon."}
                        </div>
                    ) : (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {posts.map((p, idx) => {
                                const title = isEs ? p.title_es : p.title_en;
                                const excerpt = isEs ? p.excerpt_es : p.excerpt_en;
                                const readTime = isEs ? p.read_time_es : p.read_time_en;
                                return (
                                    <motion.article
                                        key={p.post_id}
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: idx * 0.04 }}
                                        data-testid={`blog-post-card-${p.slug}`}
                                    >
                                        <Link
                                            to={`/blog/${p.slug}`}
                                            className="group block rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden h-full hover:border-[hsl(var(--primary))]/60 hover:-translate-y-0.5 transition-all"
                                        >
                                            <BlogCover src={p.cover_url} title={title} />
                                            <div className="p-6">
                                                <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase text-[hsl(var(--muted-foreground))]">
                                                    <span>{fmtDate(p.published_at)}</span>
                                                    {readTime ? (
                                                        <>
                                                            <span>·</span>
                                                            <span className="inline-flex items-center gap-1">
                                                                <Clock className="w-3 h-3" /> {readTime} min
                                                            </span>
                                                        </>
                                                    ) : null}
                                                </div>
                                                <h2 className="mt-3 font-display font-bold text-[hsl(var(--foreground))] text-lg leading-snug tracking-tight line-clamp-3 group-hover:text-[hsl(var(--primary))] transition-colors">
                                                    {title}
                                                </h2>
                                                {excerpt ? (
                                                    <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed line-clamp-3">
                                                        {excerpt}
                                                    </p>
                                                ) : null}
                                                {p.tags?.length ? (
                                                    <div className="mt-4 flex flex-wrap gap-1.5">
                                                        {p.tags.slice(0, 3).map((tg) => (
                                                            <span
                                                                key={tg}
                                                                className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"
                                                            >
                                                                {tg}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : null}
                                                <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--primary))] group-hover:gap-2.5 transition-all">
                                                    {isEs ? "Leer" : "Read"} <ArrowRight className="w-3.5 h-3.5" />
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.article>
                                );
                            })}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default Blog;
